import axios from 'axios';
import { getRegStatus } from '@/utils/a2pRegistration';

// Set axios base URL like other TeamIO services
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

const state = {
  currentRegistration: null,
  registrationStatus: null,
  loading: false,
  error: null,
  phoneNumbers: [],
  availableNumbers: [],
  phoneNumbersLoading: false
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_REGISTRATION(state, registration) {
    state.currentRegistration = registration;
  },
  SET_REGISTRATION_STATUS(state, status) {
    state.registrationStatus = status;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Clear registration state when switching sites (Super Admin workflow)
  CLEAR_REGISTRATION(state) {
    state.currentRegistration = null;
    state.registrationStatus = null;
    state.error = null;
  },
  SET_PHONE_NUMBERS(state, phoneNumbers) {
    state.phoneNumbers = phoneNumbers;
  },
  SET_AVAILABLE_NUMBERS(state, availableNumbers) {
    state.availableNumbers = availableNumbers;
  },
  SET_PHONE_NUMBERS_LOADING(state, loading) {
    state.phoneNumbersLoading = loading;
  }
};

/**
 * Shared caller for the POST /a2p-registration endpoint.
 *
 * Every A2P action hit the same endpoint with the same envelope shape, the same
 * Super-Admin role gate, and the same createSuccessResponse `.body` unwrap; this
 * centralizes that boilerplate so the actions only own their own commits and
 * return shapes.
 *
 * @param {Object} ctx                 Vuex context slice.
 * @param {Object} ctx.rootState       Root state (customer.siteID / userID / user.Role).
 * @param {Object} opts
 * @param {string} opts.action         The `action` field the backend router dispatches on.
 * @param {*}      [opts.data]         Optional `data` payload (added to the envelope only when provided).
 * @param {Object} [opts.extras]       Extra top-level envelope fields (step, areaCode, phoneNumber, …).
 * @param {string} [opts.siteID]       Explicit siteID arg. Resolution: targetSiteID || siteID || own.
 * @param {string} [opts.targetSiteID] Super-Admin override; also drives the cross-site role gate.
 * @param {boolean}[opts.includeUserId] Whether to send teamio_userid (default true; legacy light
 *                                      actions omit it).
 * @param {Object} [opts.role]         Role gate. Either { crossSite: message } (conditional — only when
 *                                      targetSiteID differs from own site, requires Super Admin/Admin) or
 *                                      { require: [...roles], error: message } (unconditional).
 * @param {Object} [opts.cancelToken]  Optional axios cancel token.
 * @returns {Promise<Object>}          Parsed response body — createSuccessResponse `.body` is JSON-parsed
 *                                      when present, otherwise the raw response.data.
 * @throws  {Error}                    On a failed role gate or an axios/network error.
 */
async function callA2P({ rootState }, {
  action,
  data,
  extras = {},
  siteID: siteIDArg,
  targetSiteID = null,
  includeUserId = true,
  role = null,
  cancelToken = null,
}) {
  const ownSiteID = rootState.customer.siteID;
  const siteID = targetSiteID || siteIDArg || ownSiteID;
  const teamio_userid = rootState.customer.userID;
  const userRole = rootState.customer.user?.Role;

  // Role gate — unconditional (require) or cross-site conditional (crossSite).
  if (role && role.require) {
    if (!role.require.includes(userRole)) {
      throw new Error(role.error);
    }
  } else if (role && role.crossSite && targetSiteID && targetSiteID !== ownSiteID) {
    if (!['Super Admin', 'Admin'].includes(userRole)) {
      throw new Error(role.crossSite);
    }
  }

  const envelope = { action, siteID, ...extras };
  if (includeUserId) envelope.teamio_userid = teamio_userid;
  if (data !== undefined) envelope.data = data;

  const config = { crossdomain: true };
  if (cancelToken) config.cancelToken = cancelToken;

  const response = await axios.post('/a2p-registration', envelope, config);

  // Normalize the createSuccessResponse wrapper: parse `.body` only when present.
  return response.data && response.data.body ? JSON.parse(response.data.body) : response.data;
}

const actions = {
  // Clear registration state - used when Super Admin switches to different site
  clearRegistration({ commit }) {
    commit('CLEAR_REGISTRATION');
  },

  async submitA2PRegistration({ commit, rootState }, payload) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'submit',
        targetSiteID: payload.targetSiteID,
        extras: { step: 'all' },
        data: payload,
        role: { crossSite: 'Only Super Administrators can register A2P for other sites' },
      });

      if (result.success) {
        commit('SET_REGISTRATION', result.registration);
        commit('SET_REGISTRATION_STATUS', getRegStatus(result.registration));

        // Show notification if subaccount was created
        if (result.subaccount_created) {
          console.log('✅ Twilio subaccount created successfully:', result.subaccount_sid);
        }

        return {
          success: true,
          registration: result.registration,
          subaccountCreated: result.subaccount_created,
          subaccountSid: result.subaccount_sid
        };
      } else {
        throw new Error(result.message || 'Registration submission failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred during registration';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getA2PRegistrationStatus({ commit, rootState }, { registrationId = null, targetSiteID = null } = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const extras = { step: 'all' };
      if (registrationId) extras.registrationId = registrationId;

      const result = await callA2P({ rootState }, {
        action: 'status',
        targetSiteID,
        extras,
        role: { crossSite: 'Only Super Administrators can check A2P status for other sites' },
      });

      // Handle both response formats: wrapped (success/data) and direct (registration/progress)
      if (result.success) {
        // Legacy format with success wrapper
        commit('SET_REGISTRATION', result.registration);
        commit('SET_REGISTRATION_STATUS', getRegStatus(result.registration));
        return { success: true, registration: result.registration };
      } else if (result.registration) {
        // New format with direct registration data
        commit('SET_REGISTRATION', result.registration);
        commit('SET_REGISTRATION_STATUS', getRegStatus(result.registration));
        return { success: true, registration: result.registration };
      } else if (result.status === 'not_started') {
        // No registration exists yet - this is a valid state for new sites
        commit('SET_REGISTRATION', null);
        commit('SET_REGISTRATION_STATUS', 'not_started');
        return { success: true, registration: null, status: 'not_started' };
      } else {
        throw new Error(result.message || 'Failed to get registration status');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while getting status';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateA2PRegistration({ commit, rootState }, { step, data, targetSiteID = null }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'update',
        targetSiteID,
        extras: { step },
        data,
        role: { crossSite: 'Only Super Administrators can update A2P registration for other sites' },
      });

      if (result.success) {
        commit('SET_REGISTRATION', result.registration);
        commit('SET_REGISTRATION_STATUS', getRegStatus(result.registration));
        return { success: true, registration: result.registration };
      } else {
        throw new Error(result.message || 'Registration update failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred during update';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Phone Number Management Actions
  async getPhoneNumbers({ commit, rootState }, { siteID, targetSiteID = null }) {
    commit('SET_PHONE_NUMBERS_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'get_phone_numbers',
        siteID,
        targetSiteID,
        role: { crossSite: 'Only Super Administrators can manage phone numbers for other sites' },
      });

      if (result.success) {
        commit('SET_PHONE_NUMBERS', result.phoneNumbers || []);
        return { success: true, data: result.phoneNumbers || [] };
      } else {
        throw new Error(result.message || 'Failed to get phone numbers');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while getting phone numbers';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage, data: [] };
    } finally {
      commit('SET_PHONE_NUMBERS_LOADING', false);
    }
  },

  async searchAvailableNumbers({ commit, rootState }, { siteID, areaCode, targetSiteID = null }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'search_available_numbers',
        siteID,
        targetSiteID,
        extras: { areaCode },
        role: { crossSite: 'Only Super Administrators can search phone numbers for other sites' },
      });

      if (result.success) {
        commit('SET_AVAILABLE_NUMBERS', result.availableNumbers || []);
        // `pricing` must survive to the component: step 8 renders the monthly
        // cost beside each number and inside the purchase confirmation. Dropping
        // it here would silently fall the UI back to a stale figure, which is the
        // exact defect this was added to fix ($1.00 shown while the real price
        // was $1.15). See a2p-service/number-pricing.js.
        return {
          success: true,
          data: result.availableNumbers || [],
          pricing: result.pricing || null,
        };
      } else {
        throw new Error(result.message || 'Failed to search available numbers');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while searching numbers';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage, data: [] };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async purchasePhoneNumber({ commit, rootState }, { siteID, phoneNumber, targetSiteID = null }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'purchase_phone_number',
        siteID,
        targetSiteID,
        extras: { phoneNumber },
        role: { crossSite: 'Only Super Administrators can purchase phone numbers for other sites' },
      });

      if (result.success) {
        return { success: true, data: result.purchasedNumber };
      } else {
        throw new Error(result.message || 'Failed to purchase phone number');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while purchasing number';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async releasePhoneNumber({ commit, rootState }, { siteID, phoneNumberSid, targetSiteID = null }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'release_phone_number',
        siteID,
        targetSiteID,
        extras: { phoneNumberSid },
        role: { crossSite: 'Only Super Administrators can release phone numbers for other sites' },
      });

      if (result.success) {
        return { success: true, data: result };
      } else {
        throw new Error(result.message || 'Failed to release phone number');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while releasing number';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Delete a failed/rejected registration to allow starting over
  async deleteA2PRegistration({ commit, rootState }, { targetSiteID = null, registrationId }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'delete_registration',
        targetSiteID,
        extras: { registrationId },
        role: { crossSite: 'Only Super Administrators can delete A2P registration for other sites' },
      });

      if (result.success) {
        // Clear local state
        commit('CLEAR_REGISTRATION');
        return { success: true };
      } else {
        throw new Error(result.message || 'Failed to delete registration');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while deleting registration';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get all A2P registrations for SuperAdmin dashboard
  async getAllA2PRegistrations({ commit, rootState }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await callA2P({ rootState }, {
        action: 'get_all_registrations',
        siteID: 'all', // Special value to indicate we want all registrations
        includeUserId: false,
        role: { require: ['Super Admin'], error: 'Only Super Administrators can view all A2P registrations' },
      });

      if (result.success && result.registrations) {
        return {
          success: true,
          registrations: result.registrations,
          count: result.count || result.registrations.length
        };
      } else {
        throw new Error(result.message || 'Failed to get all registrations');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while getting all registrations';
      commit('SET_ERROR', errorMessage);
      return { success: false, error: errorMessage, registrations: [] };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getLiveA2PStatus({ rootState }, { targetSiteID = null } = {}) {
    try {
      return await callA2P({ rootState }, {
        action: 'get_live_status',
        targetSiteID,
        includeUserId: false,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get live A2P status';
      return { success: false, error: errorMessage };
    }
  },

  async checkSiteDatabase({ rootState }, { targetSiteID = null } = {}) {
    try {
      const data = await callA2P({ rootState }, {
        action: 'check_site_database',
        targetSiteID,
        includeUserId: false,
      });
      return { success: true, exists: data.exists, message: data.message, databaseName: data.databaseName };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to check site database';
      return { success: false, error: errorMessage };
    }
  },

  async createSiteDatabase({ rootState }, { targetSiteID = null } = {}) {
    try {
      const data = await callA2P({ rootState }, {
        action: 'create_site_database',
        targetSiteID,
        includeUserId: false,
      });
      return { success: true, created: data.created, message: data.message, databaseName: data.databaseName };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create site database';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Verify the submitted business name + representative against public records
   * (BBB, the practice website) before submitting the registration. Moved out of
   * A2PWizardReview.vue so the network call lives with the other A2P actions.
   * Mirrors the original: no teamio_userid, no role gate.
   */
  async verifyBusiness({ rootState }, { siteID, businessName, repName, website, state }) {
    try {
      return await callA2P({ rootState }, {
        action: 'verify_business',
        siteID,
        data: { businessName, repName, website, state },
        includeUserId: false,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Verification failed';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Stateless URL scan — probes reachability and (for privacy/terms) scans the page
   * text for the required SMS compliance terms. No registration row needed.
   * Accepts an optional cancelToken so callers can cancel in-flight requests when
   * the user types faster than responses come back.
   *
   * urlType: 'website' | 'privacy' | 'terms'
   */
  async checkComplianceUrl({ rootState }, { url, urlType, cancelToken = null }) {
    try {
      const siteID = rootState.customer.siteID || 'stateless';
      const data = await callA2P({ rootState }, {
        action: 'check_compliance_url',
        siteID,
        data: { url, urlType },
        includeUserId: false,
        cancelToken,
      });
      // `warnings` (2026-09-15) must survive to A2PUrlScanResult.vue — non-blocking advice such
      // as the privacy consent-data + affiliates sentence (Brester Dentistry, Twilio 30908).
      // Normalized so an older backend without the field renders nothing instead of undefined.
      return { success: true, ...data, warnings: Array.isArray(data.warnings) ? data.warnings : [] };
    } catch (error) {
      if (axios.isCancel && axios.isCancel(error)) {
        return { cancelled: true };
      }
      const errorMessage = error.response?.data?.message || error.message || 'Failed to check URL';
      return { success: false, error: errorMessage };
    }
  }
};

const getters = {
  isLoading: state => state.loading,
  hasError: state => !!state.error,
  errorMessage: state => state.error,
  currentRegistration: state => state.currentRegistration,
  registrationStatus: state => state.registrationStatus,
  isRegistrationComplete: state => state.registrationStatus === 'approved',
  isRegistrationPending: state => ['pending', 'in_progress', 'submitted'].includes(state.registrationStatus),
  phoneNumbers: state => state.phoneNumbers,
  availableNumbers: state => state.availableNumbers,
  phoneNumbersLoading: state => state.phoneNumbersLoading
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
